// src/components/EquipmentManager/EquipmentCard.jsx

import React from 'react';
import PropTypes from 'prop-types';
import { FaShoppingCart, FaEye, FaBoxOpen, FaEdit, FaTrash, FaAmazon, FaExternalLinkAlt } from 'react-icons/fa';
import StarRating from '../common/StarRating';
import styles from './EquipmentCard.module.css';

const EquipmentCard = ({ 
  equipment, 
  canManage = false, 
  onView, 
  onEdit, 
  onDelete, 
  onAssign,
  onBuyClick,
  animationDelay = 0
}) => {
  // Get primary image
  const primaryImage = equipment.images?.find(img => img.isPrimary);
  const imageUrl = primaryImage?.url || 
                   equipment.images?.[0]?.url || 
                   equipment.imageUrl || 
                   '/default-equipment.png';
  
  // Calculate availability
  const totalAssigned = equipment.assignments?.length || 0;
  const available = Math.max(0, (equipment.quantity || 0) - totalAssigned);
  const quantity = equipment.quantity || 0;
  
  // Determine availability status for styling
  const getAvailabilityStatus = () => {
    if (available === 0) return 'outOfStock';
    if (available <= 2) return 'lowStock';
    if (totalAssigned > 0) return 'assigned';
    return 'available';
  };
  
  const availabilityStatus = getAvailabilityStatus();
  
  // Get card class based on availability
  const getCardClass = () => {
    switch (availabilityStatus) {
      case 'outOfStock': return styles.cardOutOfStock;
      case 'lowStock': return styles.cardLowStock;
      case 'assigned': return styles.cardAssigned;
      default: return styles.cardAvailable;
    }
  };
  
  // Get category info
  const getCategoryDisplay = () => {
    if (equipment.category?.name) {
      return { emoji: equipment.category.icon || '📦', name: equipment.category.name };
    }
    if (equipment.categoryName) {
      return { emoji: '📦', name: equipment.categoryName };
    }
    return null;
  };
  
  const categoryInfo = getCategoryDisplay();
  
  // Check if has rating
  const hasRating = equipment.ratingSummary && equipment.ratingSummary.totalReviews > 0;
  
  const handleBuyClick = (e, linkType) => {
    e.stopPropagation();
    if (onBuyClick) {
      onBuyClick(equipment._id, linkType);
    }
    // Open link
    if (linkType === 'amazon' && equipment.purchaseLinks?.amazon) {
      window.open(equipment.purchaseLinks.amazon, '_blank', 'noopener,noreferrer');
    } else if (linkType === 'direct' && equipment.purchaseLinks?.directStore) {
      window.open(equipment.purchaseLinks.directStore, '_blank', 'noopener,noreferrer');
    }
  };
  
  const handleCardClick = () => {
    if (onView) {
      onView(equipment);
    }
  };
  
  const handleEditClick = (e) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(equipment);
    }
  };
  
  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(equipment._id);
    }
  };
  
  const handleAssignClick = (e) => {
    e.stopPropagation();
    if (onAssign) {
      onAssign(equipment);
    }
  };
  
  return (
    <div 
      className={`${styles.equipmentCard} ${getCardClass()}`} 
      onClick={handleCardClick}
      style={{ animationDelay: `${animationDelay}ms` }}
    >
      {/* Image Section */}
      <div className={styles.cardImage}>
        <img src={imageUrl} alt={equipment.equipmentName} />
        
        {/* Sponsor Badge */}
        {equipment.sponsorInfo?.logo && (
          <div className={styles.sponsorBadge} title={equipment.sponsorInfo.name}>
            <img src={equipment.sponsorInfo.logo} alt={equipment.sponsorInfo.name} />
          </div>
        )}
        
        {/* Scope Badge */}
        {equipment.scope?.level === 'club' && (
          <span className={styles.scopeBadge} title="Available to all programs in club">
            Club-wide
          </span>
        )}
        
        {/* Manager Actions (hover overlay) */}
        {canManage && (
          <div className={styles.cardActions}>
            <button 
              onClick={handleEditClick} 
              className={styles.editBtn}
              title="Edit Equipment"
            >
              <FaEdit />
            </button>
            <button 
              onClick={handleDeleteClick} 
              className={styles.deleteBtn}
              title="Delete Equipment"
            >
              <FaTrash />
            </button>
          </div>
        )}
      </div>
      
      {/* Rating Section */}
      {hasRating && (
        <div className={styles.cardRating}>
          <StarRating 
            rating={equipment.ratingSummary.averageRating} 
            size="small"
          />
          <span className={styles.ratingText}>
            {equipment.ratingSummary.averageRating.toFixed(1)}
          </span>
          <span className={styles.reviewCount}>
            ({equipment.ratingSummary.totalReviews})
          </span>
        </div>
      )}
      
      {/* Content Section */}
      <div className={styles.cardContent}>
        <h3 className={styles.equipmentName}>{equipment.equipmentName}</h3>
        <p className={styles.equipmentDesc}>
          {equipment.description?.length > 80 
            ? `${equipment.description.substring(0, 80)}...` 
            : equipment.description || 'No description available'}
        </p>
        
        {/* Category + Availability Badges Row */}
        <div className={styles.cardMetaRow}>
          {categoryInfo && (
            <span className={styles.categoryBadge}>
              <span className={styles.categoryEmoji}>{categoryInfo.emoji}</span>
              {categoryInfo.name}
            </span>
          )}
          
          {quantity > 0 && (
            <span className={`${styles.availabilityBadge} ${styles[availabilityStatus === 'available' ? 'inStock' : availabilityStatus]}`}>
              {availabilityStatus === 'outOfStock' ? '⊘ Out of Stock' : 
               availabilityStatus === 'lowStock' ? `⚠ ${available} Left` :
               `✓ ${available} Available`}
            </span>
          )}
        </div>
        
        {/* Meta Info */}
        <div className={styles.cardMeta}>
          {equipment.averagePrice && (
            <span className={styles.price}>${equipment.averagePrice}</span>
          )}
          {equipment.quantity !== undefined && totalAssigned > 0 && (
            <span className={styles.availability}>
              📋 {totalAssigned} assigned
            </span>
          )}
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className={styles.cardButtons}>
        {equipment.purchaseLinks?.amazon && (
          <button 
            onClick={(e) => handleBuyClick(e, 'amazon')} 
            className={styles.buyBtn}
            title="Buy on Amazon"
          >
            <FaAmazon /> Amazon
          </button>
        )}
        
        {equipment.purchaseLinks?.directStore && !equipment.purchaseLinks?.amazon && (
          <button 
            onClick={(e) => handleBuyClick(e, 'direct')} 
            className={styles.buyBtn}
            title="Buy Direct"
          >
            <FaExternalLinkAlt /> Buy
          </button>
        )}
        
        <button 
          onClick={(e) => { e.stopPropagation(); onView(equipment); }} 
          className={styles.detailsBtn}
        >
          <FaEye /> Details
        </button>
        
        {canManage && (
          <button 
            onClick={handleAssignClick} 
            className={styles.assignBtn}
          >
            <FaBoxOpen /> Assign
          </button>
        )}
      </div>
    </div>
  );
};

EquipmentCard.propTypes = {
  equipment: PropTypes.object.isRequired,
  canManage: PropTypes.bool,
  onView: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onAssign: PropTypes.func,
  onBuyClick: PropTypes.func
};

export default EquipmentCard;

